import { useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import { scrollToAnchor } from '../../utils/scrollToAnchor';

interface VideoSectionProps {
  videoId?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  title?: string;
  description?: string;
  linkTo?: string;
  linkText?: string;
  reversed?: boolean;
}

/**
 * YouTube Facade — shows a lightweight thumbnail + play button instead of
 * loading the full YouTube iframe (~900 KB JS each). The real iframe only
 * loads when the user clicks play. This saves ~2,600 KB and ~1,500 ms of
 * main-thread time on pages with multiple videos.
 */
function YouTubeFacade({ videoId, title }: { videoId: string; title: string }) {
  const [activated, setActivated] = useState(false);

  if (activated) {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    );
  }

  // Thumbnail: YouTube provides these for free at multiple resolutions
  // sddefault (640×480) is a good balance of quality vs size
  const thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/sddefault.jpg`;

  return (
    <button
      type="button"
      onClick={() => setActivated(true)}
      className="relative w-full h-full group cursor-pointer bg-black"
      aria-label={`Play video: ${title}`}
    >
      <img
        src={thumbnailUrl}
        alt={title}
        width={640}
        height={480}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
      />
      {/* YouTube-style play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-11 bg-red-600 rounded-xl flex items-center justify-center shadow-lg group-hover:bg-red-700 group-hover:scale-110 transition-all">
          <svg className="w-6 h-6 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    </button>
  );
}

/**
 * Self-hosted video facade — shows thumbnail (or branded placeholder) + play button.
 * Only loads the video when the user clicks play (avoids heavy preload on page load).
 * Exported so it can be reused in other components (e.g. EducationPage).
 */
export function UploadedVideoFacade({ videoUrl, thumbnailUrl, title }: { videoUrl: string; thumbnailUrl?: string; title: string }) {
  const [activated, setActivated] = useState(false);

  if (activated) {
    return (
      <video
        src={videoUrl}
        controls
        autoPlay
        className="w-full h-full object-cover"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setActivated(true)}
      className="relative w-full h-full group cursor-pointer bg-gray-900"
      aria-label={`Play video: ${title}`}
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={title}
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-primary/20 via-emerald-900/30 to-teal-900/40 flex items-center justify-center">
          <div className="absolute inset-0 opacity-[0.08]">
            <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full bg-primary" />
            <div className="absolute -left-4 -bottom-4 w-32 h-32 rounded-full bg-teal-500" />
          </div>
        </div>
      )}
      {/* PropInvest-branded play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:bg-primary/90 transition-all">
          <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    </button>
  );
}

export default function VideoSection({
  videoId,
  videoUrl,
  thumbnailUrl,
  title,
  description,
  linkTo,
  linkText,
  reversed = false,
}: VideoSectionProps) {
  // Strip query params that may be appended to raw video IDs
  const cleanId = videoId ? videoId.trim().split(/[?&#]/)[0] : '';

  const videoEmbed = (
    <Reveal variant="scale">
      {cleanId ? (
        <div className="aspect-video rounded-xl overflow-hidden shadow-card bg-gray-100">
          <YouTubeFacade videoId={cleanId} title={title || 'Video'} />
        </div>
      ) : videoUrl ? (
        <div className="aspect-video rounded-xl overflow-hidden shadow-card bg-gray-900">
          <UploadedVideoFacade videoUrl={videoUrl} thumbnailUrl={thumbnailUrl} title={title || 'Video'} />
        </div>
      ) : (
        <div className="aspect-video rounded-xl overflow-hidden shadow-card bg-gradient-to-br from-primary/10 via-emerald-50 to-teal-50 flex items-center justify-center relative">
          <div className="absolute inset-0 opacity-[0.06]">
            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-primary" />
            <div className="absolute -left-4 -bottom-4 w-24 h-24 rounded-full bg-teal-500" />
          </div>
          <div className="relative w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center">
            <svg className="w-7 h-7 text-primary ml-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      )}
    </Reveal>
  );

  // If no title/description, just render the video
  const hasText = title || description || (linkTo && linkText);
  if (!hasText) {
    return videoEmbed;
  }

  const textContent = (
    <div className="flex flex-col justify-center">
      {title && <Reveal variant="up"><h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{title}</h2></Reveal>}
      {description && <p className="text-lg text-gray-600 mb-6">{description}</p>}
      {linkTo && linkText && (
        linkTo.startsWith('#') ? (
          // In-page anchor (e.g. the home page's "Get Now" → strategy form):
          // scroll instead of routing; scrollToAnchor corrects for lazy-load
          // layout shift on the way down.
          <button
            type="button"
            onClick={() => scrollToAnchor(linkTo.slice(1))}
            className="btn-primary self-start"
          >
            {linkText}
          </button>
        ) : linkTo.startsWith('http') ? (
          <a
            href={linkTo}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary self-start"
          >
            {linkText}
          </a>
        ) : (
          <Link to={linkTo} className="btn-primary self-start">
            {linkText}
          </Link>
        )
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
      {reversed ? (
        <>
          {textContent}
          {videoEmbed}
        </>
      ) : (
        <>
          {videoEmbed}
          {textContent}
        </>
      )}
    </div>
  );
}
